import React from "react";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { focusRing } from "../Exams/TestBuilder/ui";
import { Codes, DIGITS, FieldError, labelClass, variantCode } from "./bts";

const digitClass = (selected, taken) =>
  `relative inline-flex h-11 w-11 items-center justify-center rounded-lg text-base font-semibold tabular-nums ring-1 ring-inset transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 sm:h-10 sm:w-10 ${focusRing} ${
    selected
      ? taken
        ? "bg-amber-700 text-white shadow-sm ring-amber-700 enabled:hover:bg-amber-800"
        : "bg-blue-600 text-white shadow-sm ring-blue-600 enabled:hover:bg-blue-700"
      : taken
      ? "bg-amber-50 text-amber-900 ring-amber-300 enabled:hover:bg-amber-100"
      : "bg-white text-slate-700 ring-slate-300 enabled:hover:bg-slate-50 enabled:hover:text-slate-900"
  }`;

/**
 * The variant digits of a booklet (1–9, each a toggle) and the codes they make ({БТС}{grade}{digit}). Digits
 * other active booklets of the same BTS and grade use (`taken`: Map digit → [{uuid, name}]) are marked: the
 * activation would refuse their codes. A failed check focuses `${id}-group` (its first digit).
 */
export default function VariantDigits({ id, value, onChange, round, grade, taken, disabled = false, error }) {
  const [t] = useTranslation();
  const selected = [...(value || [])].sort((a, b) => a - b);
  const codes = selected.map((digit) => variantCode(round, grade, digit)).filter(Boolean);
  const takenCodes = selected.filter((digit) => taken?.has(digit)).map((digit) => variantCode(round, grade, digit));
  const clashes = selected.filter((digit) => taken?.has(digit));
  const otherTaken = DIGITS.filter((digit) => taken?.has(digit) && !selected.includes(digit));
  const toggle = (digit) =>
    onChange(selected.includes(digit) ? selected.filter((d) => d !== digit) : [...selected, digit].sort((a, b) => a - b));

  return (
    <div>
      <span id={`${id}-label`} className={labelClass}>
        {t("bts.digits.label")}
      </span>
      <div
        id={`${id}-group`}
        role="group"
        aria-labelledby={`${id}-label`}
        aria-describedby={error ? `${id}-codes ${id}-error` : `${id}-codes`}
        className="flex flex-wrap gap-1.5"
      >
        {DIGITS.map((digit) => {
          const on = selected.includes(digit);
          const busy = taken?.has(digit);
          const code = variantCode(round, grade, digit);
          return (
            <button
              key={digit}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => toggle(digit)}
              title={
                busy
                  ? t("bts.digits.taken_title", { code: code || digit, names: taken.get(digit).map((b) => b.name).join(", ") })
                  : code || undefined
              }
              aria-label={
                busy
                  ? t("bts.digits.digit_taken_label", { digit, code: code || digit })
                  : code
                  ? t("bts.digits.digit_label", { digit, code })
                  : String(digit)
              }
              className={digitClass(on, busy)}
            >
              {digit}
              {busy && (
                <span
                  className={`absolute right-1 top-1 h-1.5 w-1.5 rounded-full ${on ? "bg-white" : "bg-amber-500"}`}
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>
      <div id={`${id}-codes`} className="mt-2 space-y-1.5 text-sm text-slate-600">
        {/* What a digit is for, while it can be chosen. */}
        {!disabled && <p className="text-slate-500">{t("bts.digits.explain")}</p>}
        {codes.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{t("bts.digits.codes", { count: codes.length })}</span>
            <Codes codes={codes} taken={takenCodes} />
          </div>
        ) : selected.length > 0 ? (
          <p className="text-slate-500">{t("bts.digits.codes_need_grade")}</p>
        ) : null}
        {clashes.length > 0 && (
          <p className="flex items-start gap-1.5 text-amber-800">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {t("bts.digits.clash", {
                count: clashes.length,
                list: clashes
                  .map((digit) => `${variantCode(round, grade, digit)} — ${taken.get(digit).map((b) => `«${b.name}»`).join(", ")}`)
                  .join("; "),
              })}
            </span>
          </p>
        )}
        {otherTaken.length > 0 && <p className="text-slate-500">{t("bts.digits.other_taken", { digits: otherTaken.join(", ") })}</p>}
      </div>
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
}
